package org.matthewo.api

import kotlin.jvm.JvmName
import kotlinx.serialization.Serializable
import kotlin.math.roundToInt
import org.matthewo.symbolprocessor.Period.*
import org.matthewo.schema.MetricsMeta
import org.matthewo.schema.games.*
import org.matthewo.math.plus
import org.matthewo.math.convert

@Serializable
data class MetricsFreightFrenzyAPI<N : Number>(
    override val total: N,
    override val npTotal: N,
    override val auto: N,
    override val teleop: N,
    override val endgame: N,
    override val penalty: N,
    override val apBonus: N,
    override val apStorage: N,
    override val apLevel1: N,
    override val apLevel2: N,
    override val apLevel3: N,
    override val apCarousel: N,
    override val apNavigation: N,
    override val tpStorage: N,
    override val tpLevel1: N,
    override val tpLevel2: N,
    override val tpLevel3: N,
    override val tpShared: N,
    override val epDelivered: N,
    override val epAllianceBalance: N,
    override val epSharedTip: N,
    override val epCap: N,
    override val epPark: N,
) : MetricsAPI<N>, MetricsFreightFrenzy<N> {
    constructor(team1: MetricsFreightFrenzy<N>, team2: MetricsFreightFrenzy<N>) : this(
        total = team1.total + team2.total,
        npTotal = team1.npTotal + team2.npTotal,
        auto = team1.auto + team2.auto,
        teleop = team1.teleop + team2.teleop,
        endgame = team1.endgame + team2.endgame,
        penalty = team1.penalty + team2.penalty,
        apBonus = team1.apBonus + team2.apBonus,
        apStorage = team1.apStorage + team2.apStorage,
        apLevel1 = team1.apLevel1 + team2.apLevel1,
        apLevel2 = team1.apLevel2 + team2.apLevel2,
        apLevel3 = team1.apLevel3 + team2.apLevel3,
        apCarousel = team1.apCarousel + team2.apCarousel,
        apNavigation = team1.apNavigation + team2.apNavigation,
        tpStorage = team1.tpStorage + team2.tpStorage,
        tpLevel1 = team1.tpLevel1 + team2.tpLevel1,
        tpLevel2 = team1.tpLevel2 + team2.tpLevel2,
        tpLevel3 = team1.tpLevel3 + team2.tpLevel3,
        tpShared = team1.tpShared + team2.tpShared,
        epDelivered = team1.epDelivered + team2.epDelivered,
        epAllianceBalance = team1.epAllianceBalance + team2.epAllianceBalance,
        epSharedTip = team1.epSharedTip + team2.epSharedTip,
        epCap = team1.epCap + team2.epCap,
        epPark = team1.epPark + team2.epPark,
    )
    
    companion object {
        inline operator fun <reified N : Number> invoke(team1: MetricsFreightFrenzy<*>, team2: MetricsFreightFrenzy<*>) = MetricsFreightFrenzyAPI(
            total = team1.total.convert<N>() + team2.total.convert<N>(),
            npTotal = team1.npTotal.convert<N>() + team2.npTotal.convert<N>(),
            auto = team1.auto.convert<N>() + team2.auto.convert<N>(),
            teleop = team1.teleop.convert<N>() + team2.teleop.convert<N>(),
            endgame = team1.endgame.convert<N>() + team2.endgame.convert<N>(),
            penalty = team1.penalty.convert<N>() + team2.penalty.convert<N>(),
            apBonus = team1.apBonus.convert<N>() + team2.apBonus.convert<N>(),
            apStorage = team1.apStorage.convert<N>() + team2.apStorage.convert<N>(),
            apLevel1 = team1.apLevel1.convert<N>() + team2.apLevel1.convert<N>(),
            apLevel2 = team1.apLevel2.convert<N>() + team2.apLevel2.convert<N>(),
            apLevel3 = team1.apLevel3.convert<N>() + team2.apLevel3.convert<N>(),
            apCarousel = team1.apCarousel.convert<N>() + team2.apCarousel.convert<N>(),
            apNavigation = team1.apNavigation.convert<N>() + team2.apNavigation.convert<N>(),
            tpStorage = team1.tpStorage.convert<N>() + team2.tpStorage.convert<N>(),
            tpLevel1 = team1.tpLevel1.convert<N>() + team2.tpLevel1.convert<N>(),
            tpLevel2 = team1.tpLevel2.convert<N>() + team2.tpLevel2.convert<N>(),
            tpLevel3 = team1.tpLevel3.convert<N>() + team2.tpLevel3.convert<N>(),
            tpShared = team1.tpShared.convert<N>() + team2.tpShared.convert<N>(),
            epDelivered = team1.epDelivered.convert<N>() + team2.epDelivered.convert<N>(),
            epAllianceBalance = team1.epAllianceBalance.convert<N>() + team2.epAllianceBalance.convert<N>(),
            epSharedTip = team1.epSharedTip.convert<N>() + team2.epSharedTip.convert<N>(),
            epCap = team1.epCap.convert<N>() + team2.epCap.convert<N>(),
            epPark = team1.epPark.convert<N>() + team2.epPark.convert<N>(),
        )
    }
}

inline fun <reified N : Number> MetricsFreightFrenzy<*>.convert() = serialize { convert<N>() }

fun <N : Number> MetricsFreightFrenzy<N>.serialize() = MetricsFreightFrenzyAPI(
    total = total,
    npTotal = npTotal,
    auto = auto,
    teleop = teleop,
    endgame = endgame,
    penalty = penalty,
    apBonus = apBonus,
    apStorage = apStorage,
    apLevel1 = apLevel1,
    apLevel2 = apLevel2,
    apLevel3 = apLevel3,
    apCarousel = apCarousel,
    apNavigation = apNavigation,
    tpStorage = tpStorage,
    tpLevel1 = tpLevel1,
    tpLevel2 = tpLevel2,
    tpLevel3 = tpLevel3,
    tpShared = tpShared,
    epDelivered = epDelivered,
    epAllianceBalance = epAllianceBalance,
    epSharedTip = epSharedTip,
    epCap = epCap,
    epPark = epPark,
)

@JvmName("serializeGeneric")
fun <T : Number, R : Number> MetricsFreightFrenzy<T>.serialize(serializer: T.() -> R) = MetricsFreightFrenzyAPI(
    total = total.serializer(),
    npTotal = npTotal.serializer(),
    auto = auto.serializer(),
    teleop = teleop.serializer(),
    endgame = endgame.serializer(),
    penalty = penalty.serializer(),
    apBonus = apBonus.serializer(),
    apStorage = apStorage.serializer(),
    apLevel1 = apLevel1.serializer(),
    apLevel2 = apLevel2.serializer(),
    apLevel3 = apLevel3.serializer(),
    apCarousel = apCarousel.serializer(),
    apNavigation = apNavigation.serializer(),
    tpStorage = tpStorage.serializer(),
    tpLevel1 = tpLevel1.serializer(),
    tpLevel2 = tpLevel2.serializer(),
    tpLevel3 = tpLevel3.serializer(),
    tpShared = tpShared.serializer(),
    epDelivered = epDelivered.serializer(),
    epAllianceBalance = epAllianceBalance.serializer(),
    epSharedTip = epSharedTip.serializer(),
    epCap = epCap.serializer(),
    epPark = epPark.serializer(),
)

@JvmName("serializeDouble")
fun MetricsFreightFrenzy<Double>.serialize() = serialize { roundToInt().toShort() }

@JvmName("serializeNumber")
fun MetricsFreightFrenzy<Number>.serialize() = serialize { toShort() }

fun MetricsFreightFrenzy<*>.stringify() = """
        total: $total
        npTotal: $npTotal
        auto: $auto
        teleop: $teleop
        endgame: $endgame
        penalty: $penalty
        apBonus: $apBonus
        apStorage: $apStorage
        apLevel1: $apLevel1
        apLevel2: $apLevel2
        apLevel3: $apLevel3
        apCarousel: $apCarousel
        apNavigation: $apNavigation
        tpStorage: $tpStorage
        tpLevel1: $tpLevel1
        tpLevel2: $tpLevel2
        tpLevel3: $tpLevel3
        tpShared: $tpShared
        epDelivered: $epDelivered
        epAllianceBalance: $epAllianceBalance
        epSharedTip: $epSharedTip
        epCap: $epCap
        epPark: $epPark
    """.trimIndent()

object MetricsFreightFrenzyMeta : MetricsMeta<MetricsFreightFrenzy<*>> {
    override val names = linkedMapOf(
        MetricsFreightFrenzy<*>::total to "Total",
        MetricsFreightFrenzy<*>::npTotal to "NP Total",
        MetricsFreightFrenzy<*>::auto to "Auto",
        MetricsFreightFrenzy<*>::teleop to "Teleop",
        MetricsFreightFrenzy<*>::endgame to "Endgame",
        MetricsFreightFrenzy<*>::penalty to "Penalty",
        MetricsFreightFrenzy<*>::apBonus to "Bonus",
        MetricsFreightFrenzy<*>::apStorage to "Storage",
        MetricsFreightFrenzy<*>::apLevel1 to "Level 1",
        MetricsFreightFrenzy<*>::apLevel2 to "Level 2",
        MetricsFreightFrenzy<*>::apLevel3 to "Level 3",
        MetricsFreightFrenzy<*>::apCarousel to "Carousel",
        MetricsFreightFrenzy<*>::apNavigation to "Navigation",
        MetricsFreightFrenzy<*>::tpStorage to "Storage",
        MetricsFreightFrenzy<*>::tpLevel1 to "Level 1",
        MetricsFreightFrenzy<*>::tpLevel2 to "Level 2",
        MetricsFreightFrenzy<*>::tpLevel3 to "Level 3",
        MetricsFreightFrenzy<*>::tpShared to "Shared",
        MetricsFreightFrenzy<*>::epDelivered to "Duck Deliver",
        MetricsFreightFrenzy<*>::epAllianceBalance to "Alliance Balance",
        MetricsFreightFrenzy<*>::epSharedTip to "Shared Tip",
        MetricsFreightFrenzy<*>::epCap to "Cap",
        MetricsFreightFrenzy<*>::epPark to "Park",
    )
    
    override val periods = linkedMapOf(
        MetricsFreightFrenzy<*>::total to ALL,
        MetricsFreightFrenzy<*>::npTotal to ALL,
        MetricsFreightFrenzy<*>::auto to AUTO,
        MetricsFreightFrenzy<*>::teleop to TELEOP,
        MetricsFreightFrenzy<*>::endgame to ENDGAME,
        MetricsFreightFrenzy<*>::penalty to ALL,
        MetricsFreightFrenzy<*>::apBonus to AUTO,
        MetricsFreightFrenzy<*>::apStorage to AUTO,
        MetricsFreightFrenzy<*>::apLevel1 to AUTO,
        MetricsFreightFrenzy<*>::apLevel2 to AUTO,
        MetricsFreightFrenzy<*>::apLevel3 to AUTO,
        MetricsFreightFrenzy<*>::apCarousel to AUTO,
        MetricsFreightFrenzy<*>::apNavigation to AUTO,
        MetricsFreightFrenzy<*>::tpStorage to TELEOP,
        MetricsFreightFrenzy<*>::tpLevel1 to TELEOP,
        MetricsFreightFrenzy<*>::tpLevel2 to TELEOP,
        MetricsFreightFrenzy<*>::tpLevel3 to TELEOP,
        MetricsFreightFrenzy<*>::tpShared to TELEOP,
        MetricsFreightFrenzy<*>::epDelivered to ENDGAME,
        MetricsFreightFrenzy<*>::epAllianceBalance to ENDGAME,
        MetricsFreightFrenzy<*>::epSharedTip to ENDGAME,
        MetricsFreightFrenzy<*>::epCap to ENDGAME,
        MetricsFreightFrenzy<*>::epPark to ENDGAME,
    )
}
